'use client'

import { useGyymClient } from '@gyym/sdk-react'
import { Link, useDialog } from '@gyym/ui'
import * as Sentry from '@sentry/nextjs'
import { useRouter, useSearchParams } from 'next/navigation'
import React from 'react'

import {
  type FormValues,
  SignupForm,
  type SubmitContext,
} from '@/components/Forms/SignupForm'
import { Breadcrumbs, Content, Heading, Page } from '@/components/Layouts/Page'
import { useAnalyticsParams } from '@/hooks/useAnalytics'
import { pushSignupEvent } from '@/lib/googleTagManager'
import { useAuthUser, useIntendedPath } from '@/modules/Auth'

// NOTE: This is the path to redirect after signup, and is NOT the same path as the email verification link
const DEFAULT_POST_SIGNUP_PATH = '/facilities?sortBy=distance&order=asc'

export default function SignupPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const client = useGyymClient()
  const intended = useIntendedPath()
  const analyticsParams = useAnalyticsParams()
  const { openDialog } = useDialog()
  const { mutate } = useAuthUser()

  const isLineSignup = searchParams?.get('line_signup') === 'true'
  const email = searchParams?.get('email') ?? ''
  const referralSubjectCode =
    searchParams?.get('referral_subject_code') || undefined
  const campaignCode = searchParams?.get('campaign_code') ?? ''
  const socialSignupToken =
    searchParams?.get('social_signup_token') ?? undefined

  const isReferral = referralSubjectCode !== undefined

  const onSubmit = React.useCallback(
    async (
      { notification, ...rest }: FormValues,
      { setError }: SubmitContext,
    ) => {
      try {
        // Register user
        const user = await client.auth.user.register({
          ...rest,
          referralSubjectCode,
          socialSignupToken,
          ...analyticsParams,
          intended,
          optIns: notification
            ? {
                campaign: true,
                newPrograms: true,
                featureUpdates: true,
              }
            : {
                campaign: false,
                newPrograms: false,
                featureUpdates: false,
              },
        })

        // Mutate auth user and revalidate
        mutate(user, true)

        // Push signup event to GTM
        pushSignupEvent(user)

        // Show mail sent dialog
        openDialog({
          title: '登録メールを送信しました',
          body: (
            <>
              まだ登録は完了していません。
              <br />
              登録メールをご確認いただき、メールに記載のURLから登録を完了させてください。
            </>
          ),
          onClose() {
            router.push(DEFAULT_POST_SIGNUP_PATH)
          },
          disableBackdropClose: true,
        })
      } catch (e) {
        type Errors = { [key: string]: string[] }
        const error = e as unknown as {
          message: string
          errors: Errors | undefined
        }
        const errors = error.errors ?? ({} as Errors)

        Object.keys(errors).forEach((key) => {
          errors[key]?.forEach((message) => {
            setError(key as keyof FormValues, {
              type: 'custom',
              message,
            })
          })
        })

        if (
          !error.errors &&
          error.message ===
            'ソーシャルログイン認証情報のメールアドレスと入力されたメールアドレスが一致しません'
        ) {
          setError('email', {
            type: 'custom',
            message: error.message,
          })
        }
      }
    },
    [
      client.auth.user,
      referralSubjectCode,
      socialSignupToken,
      analyticsParams,
      mutate,
      openDialog,
      intended,
      router,
    ],
  )

  React.useEffect(() => {
    if (!referralSubjectCode) return

    const check = async () => {
      try {
        const { exists } = await client.user.referrals.validateCode(
          referralSubjectCode,
        )

        if (!exists) {
          openDialog({
            title: '招待コードが無効です',
            body: 'OKボタンで閉じてトップへ',
            onClose: () => {
              router.push('/')
            },
          })
        }
      } catch (e) {
        Sentry.captureException(e)
      }
    }

    check()
  }, [referralSubjectCode, openDialog, router, client.user.referrals])

  return (
    <Page>
      <Breadcrumbs
        breadcrumbs={[
          {
            label: 'TOP',
            href: '/',
          },
          {
            label: '新規登録',
            href: '/signup',
          },
        ]}
      />

      <Heading
        paragraph={
          isReferral
            ? '招待ポイントは、初回プログラム利用確定で1,000pt付与されます。'
            : undefined
        }
      >
        {!isReferral ? '新規登録' : '招待ユーザー新規登録'}
      </Heading>

      <Content gap={10}>
        <SignupForm
          values={{ email, campaignCode }}
          isSnsRegistration={isLineSignup}
          onSubmit={onSubmit}
        />
        <div className="flex w-full items-center justify-center">
          <Link href="/login" variant="primary">
            ログインはこちら
          </Link>
        </div>
      </Content>
    </Page>
  )
}
